import { Box, IconButton, InputAdornment, InputBase } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import IconWrapper from "../../common/components/functional/icons/IconWrapper";
import { ReactComponent as ArrowsRightFilledIcon } from "../../common/icons/arrows-right-filled.svg";
import { ReactComponent as SearchIcon } from "../../common/icons/search-icon.svg";
import { searchConversations } from "./messagingActions";
import {
  clearConversationSearch,
  selectConversationSearchQuery,
} from "./messagingSlice";

type Props = {
  compact?: boolean;
};

const ConversationSearchField = ({ compact = false }: Props) => {
  const { t } = useTranslation("messaging");
  const dispatch = useAppDispatch();
  const submittedQuery = useAppSelector(selectConversationSearchQuery);
  const [value, setValue] = useState(submittedQuery ?? "");

  useEffect(() => {
    setValue(submittedQuery ?? "");
  }, [submittedQuery]);

  const submit = () => {
    const search = value.trim();
    if (!search) {
      dispatch(clearConversationSearch());
      return;
    }
    dispatch(searchConversations({ search, isInitial: true }));
  };

  const onChange = (nextValue: string) => {
    setValue(nextValue);
    if (!nextValue.trim() && submittedQuery) {
      dispatch(clearConversationSearch());
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        width: "100%",
        height: "100%",
        minWidth: 0,
      }}
    >
      <InputBase
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== "Enter") return;
          event.preventDefault();
          submit();
        }}
        placeholder={
          compact ? t("searchByParticipantShort") : t("searchByParticipant")
        }
        startAdornment={
          <InputAdornment position="start">
            <IconWrapper
              icon={SearchIcon}
              sx={{
                fontSize: 20,
                "& path": { fill: "black", fillOpacity: 0.52 },
              }}
            />
          </InputAdornment>
        }
        inputProps={{
          "aria-label": compact
            ? t("searchByParticipantShort")
            : t("searchByParticipant"),
        }}
        sx={{
          flex: 1,
          minWidth: 0,
          "& .MuiInputBase-input": {
            padding: 0,
            textOverflow: "ellipsis",
          },
        }}
      />
      <IconButton
        aria-label={t("search")}
        onClick={submit}
        sx={{
          flexShrink: 0,
          width: compact ? 36 : 40,
          height: compact ? 36 : 40,
          marginRight: "4px",
        }}
      >
        <IconWrapper
          icon={ArrowsRightFilledIcon}
          sx={{
            fontSize: 20,
            "& path": { fill: "black", fillOpacity: 0.52 },
          }}
        />
      </IconButton>
    </Box>
  );
};

export default ConversationSearchField;
